/*
 * Night Ops, as the app defines it.
 *
 * The dashboard is a different surface with a different job, but an organiser switching between the
 * two should not feel they have changed product. The values are copied from the app's palette
 * rather than re-picked by eye, and the four affiliation colours are reserved here for the same
 * reason they are there: a blue thing on the map means friendly, so nothing else may be blue.
 */

:root {
  --base: #0d1210;
  --elevated: #141b18;
  --raised: #1b2420;
  --overlay: #212c27;
  --outline: #2d3a34;
  --outline-strong: #3d4d45;

  --text-primary: #e8efe9;
  --text-secondary: #a9b8ae;
  --text-tertiary: #7d8f84;

  --accent: #7cc576;
  --accent-dim: #4a7a46;

  --success: #5fbf5a;
  --warning: #e8c34d;
  --danger: #e24a42;
  --info: #5b9fe0;

  /* Reserved: APP-6 affiliation. Symbol fills only, never chrome. */
  --affiliation-friend: #5b8fe0;
  --affiliation-hostile: #e24a42;
  --affiliation-neutral: #4fa85e;
  --affiliation-unknown: #e5c94b;

  --radius: 10px;
  --radius-lg: 14px;

  /* A desktop pointer is precise, so controls can be smaller than the app's gloved-hand sizes,
     but not so small that a tired marshal misses them at two in the morning. */
  --control-height: 36px;

  color-scheme: dark;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
}

* {
  box-sizing: border-box;
}

html,
body,
#root {
  height: 100%;
  margin: 0;
}

body {
  background: var(--base);
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.5;
}

h1,
h2,
h3 {
  margin: 0;
  font-weight: 600;
  letter-spacing: -0.01em;
}

h1 {
  font-size: 20px;
}
h2 {
  font-size: 16px;
}
h3 {
  font-size: 14px;
}

a {
  color: var(--accent);
}

button {
  font: inherit;
  cursor: pointer;
  border-radius: var(--radius);
  border: 1px solid transparent;
  height: var(--control-height);
  padding: 0 14px;
  background: var(--accent);
  color: #0b140b;
  font-weight: 600;
  transition: filter 120ms ease;
}

button:hover:not(:disabled) {
  filter: brightness(1.1);
}

button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

button.secondary {
  background: transparent;
  border-color: var(--outline-strong);
  color: var(--text-primary);
}

button.danger {
  background: var(--danger);
  color: #fff;
}

button.ghost {
  background: transparent;
  border-color: transparent;
  color: var(--text-secondary);
  padding: 0 8px;
}

input,
select,
textarea {
  font: inherit;
  width: 100%;
  height: var(--control-height);
  padding: 0 10px;
  border-radius: var(--radius);
  border: 1px solid var(--outline);
  background: var(--raised);
  color: var(--text-primary);
}

textarea {
  height: auto;
  min-height: 72px;
  padding: 8px 10px;
  resize: vertical;
}

input:focus,
select:focus,
textarea:focus {
  outline: 2px solid var(--accent-dim);
  outline-offset: 1px;
  border-color: var(--accent-dim);
}

label {
  display: block;
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 4px;
}

.field {
  margin-bottom: 14px;
}

.card {
  background: var(--elevated);
  border: 1px solid var(--outline);
  border-radius: var(--radius-lg);
  padding: 16px;
}

.muted {
  color: var(--text-secondary);
}

.tertiary {
  color: var(--text-tertiary);
  font-size: 12px;
}

.row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.spread {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* A pill always carries a word, never a colour alone: roughly one man in twelve cannot separate
   red from green, and this hobby is overwhelmingly men. */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  border: 1px solid;
}

.pill.neutral {
  color: var(--text-secondary);
  border-color: var(--outline-strong);
  background: rgba(255, 255, 255, 0.03);
}
.pill.success {
  color: var(--success);
  border-color: rgba(95, 191, 90, 0.4);
  background: rgba(95, 191, 90, 0.12);
}
.pill.warning {
  color: var(--warning);
  border-color: rgba(232, 195, 77, 0.4);
  background: rgba(232, 195, 77, 0.12);
}
.pill.danger {
  color: var(--danger);
  border-color: rgba(226, 74, 66, 0.45);
  background: rgba(226, 74, 66, 0.14);
}
.pill.info {
  color: var(--info);
  border-color: rgba(91, 159, 224, 0.4);
  background: rgba(91, 159, 224, 0.12);
}

.banner {
  padding: 10px 12px;
  border-radius: var(--radius);
  border: 1px solid;
  font-size: 13px;
}

.banner.danger {
  color: var(--danger);
  border-color: rgba(226, 74, 66, 0.45);
  background: rgba(226, 74, 66, 0.1);
}

.banner.warning {
  color: var(--warning);
  border-color: rgba(232, 195, 77, 0.45);
  background: rgba(232, 195, 77, 0.1);
}

.banner.info {
  color: var(--info);
  border-color: rgba(91, 159, 224, 0.4);
  background: rgba(91, 159, 224, 0.1);
}

code {
  font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 0.92em;
}

/* Leaflet sits on a dark ground, and its defaults assume a light one. */
.leaflet-container {
  background: var(--base);
  font-family: inherit;
}

.leaflet-control-attribution {
  background: rgba(13, 18, 16, 0.82) !important;
  color: var(--text-tertiary) !important;
  font-size: 10px !important;
}

.leaflet-control-attribution a {
  color: var(--text-secondary) !important;
}
